
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// types
import {
    BillingAdditionalServicePrice,
    CalculationT,
    EditAdditionalServiceT,
    UpdateCalculationPayload,
} from '@/core/types/billing.types'
import { CompanyT } from '@/core/types/companies.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { billing } from '@/core/realisations/billing.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { required, decimal } from '@vuelidate/validators'

// constants
import { ZONE_OPTIONS, SHIPMENT_TYPE_OPTIONS } from '@/core/constants/calculation.constants'
import {
    BILLING_EDIT_ADDITIONAL_SERVICES,
    BILLING_EDIT_ADDITIONAL_SERVICES_LIST,
} from '@/core/constants/billing.constants'
import { EditAdditionalServicesEnum } from '@/core/enums/billing.enums'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { companies } from '@/core/realisations/companies.realisation'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        TheButton,
    },
    props: {
        calculationId: {
            type: Number,
            default: null,
        },
        invoiceNumber: {
            type: String,
            default: null,
        },
    },
    emits: ['cancel', 'updated'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_add_additional_service: false,
            calculation: null as CalculationT | null,
            additional_services: [] as BillingAdditionalServicePrice[],
            additional_service_code: null as string | null,
        })

        const payload = reactive({
            companyId: null as number | null,
            payerCompanyId: null as number | null,
            weightInKg: null as number | null,
            volumeWeightInKg: null as number | null,
            shipmentType: null as number | null,
            zone: null as number | null,
            deliveredAt: null as string | null,
            //
            fromCityId: null as number | null,
            whereCityId: null as number | null,
            barcode: null as string | null,
            //
            declaredPrice: null as number | null,
            //
            additionalServices: [] as EditAdditionalServiceT[],
        })

        const state_form$ = useVuelidate(
            {
                companyId: { required },
                payerCompanyId: { required },
                weightInKg: { required, decimal },
                volumeWeightInKg: { required, decimal },
                shipmentType: { required },
                zone: { required },
                deliveredAt: { required },
            },
            payload
        )

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            payers: new DataState<CompanyT>({}),
        })

        const resetInfo = () => {
            state.additional_services = []
            payload.additionalServices = []
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const getParams = () => {
            if (!state.calculation) return

            let params: UpdateCalculationPayload = {
                ...payload,
                tariffCompanyId: payload.payerCompanyId as number,
                productName: state.calculation.productName,
                tariffId: state.calculation.tariffId,
                direction: state.calculation.direction,
                placesQuantity: state.calculation.placesQuantity,
                invoiceId: state.calculation.invoiceId,
                invoiceNumber: state.calculation.invoiceNumber,
                orderNumberFrom1C: state.calculation.orderNumberFrom1C,
                fromMainCity: state.calculation.fromMainCity,
                orderCreatedAt: state.calculation.orderCreatedAt.split(' ')[0],
            }

            if (state.calculation.standardParamValue) {
                params.hasDeliveryToDistributionCenter = Boolean(
                    state.calculation.standardParamValue.hasDeliveryToDistributionCenter
                )
                params.isCloseCity = state.calculation.standardParamValue.isCloseCity
                params.orderSource = state.calculation.standardParamValue.orderSource
                params.isPickup = state.calculation.standardParamValue.isPickup
                params.isSelfDelivery = state.calculation.standardParamValue.isSelfDelivery
            }

            return params
        }

        const updateCalculation = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || state.is_loading || !state.calculation) return

            try {
                state.is_loading = true

                const params = getParams()

                await billing.updateCalculation(
                    props.calculationId,
                    params as UpdateCalculationPayload
                )

                notifications.notify({ title: 'Вычисление сохранено', type: 'success' })
                emit('updated')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении вычисления произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const setInfo = () => {
            if (!state.calculation) return

            payload.companyId = state.calculation.companyId
            payload.payerCompanyId = state.calculation.payerCompanyId
            payload.weightInKg = state.calculation.weight
            payload.volumeWeightInKg = state.calculation.volumeWeight
            payload.shipmentType = state.calculation.shipmentTypeId
            payload.zone = state.calculation.zoneId
            payload.zone = state.calculation.zoneId
            payload.declaredPrice = state.calculation.standardParamValue.declaredPrice
            if (state.calculation.deliveredAt) {
                payload.deliveredAt = state.calculation.deliveredAt.split(' ')[0]
            }
            payload.barcode = state.calculation.barcode
            if (state.calculation.fromCityId) {
                payload.fromCityId = state.calculation.fromCityId.id
            }
            if (state.calculation.whereCityId) {
                payload.whereCityId = state.calculation.whereCityId.id
            }
        }

        const loadCalculation = async () => {
            try {
                state.is_loading = true

                const response = await billing.getCalculation(props.calculationId)
                state.calculation = response.data

                setInfo()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При получении вычисления произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const setAdditionalServices = () => {
            if (!state.additional_services.length) return

            const additional_services = [...BILLING_EDIT_ADDITIONAL_SERVICES]
            state.additional_services.forEach((service) => {
                const additional_service_new = additional_services.find(
                    (item) =>
                        service.additionalService && item.code === service.additionalService.name
                )

                if (additional_service_new) {
                    additional_service_new.value = service.value as number
                    additional_service_new.duration = service.duration as number
                    additional_service_new.costPerHour = service.costPerHour as number

                    payload.additionalServices.push(additional_service_new)
                }
            })
        }

        const loadAdditionalServices = async () => {
            try {
                state.is_loading = true

                const response = await billing.getAdditionalServicesPrices({
                    calculationId: props.calculationId,
                })
                state.additional_services = response.data
                setAdditionalServices()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При получении доп. услуг произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const data = await companies.getList({
                    page,
                    search: search || '',
                    isExtended: 0,
                })

                data_state.companies.list = data.data
                data_state.companies.pagination.total_pages = data.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({
                    page,
                })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadPayers = async (page: number, search?: string) => {
            data_state.payers.pagination.current_page = page

            try {
                data_state.payers.is_loading = true
                const data = await companies.getList({
                    page,
                    search: search || '',
                    isExtended: 0,
                })

                data_state.payers.list = data.data
                data_state.payers.pagination.total_pages = data.meta.last_page

                data_state.payers.is_failed = false
                data_state.payers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.payers.failure.catchError(error)

                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_failed = true
                }
            } finally {
                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_loading = false
                }
            }
        }

        const loadMorePayers = async (page: number) => {
            if (page > data_state.payers.pagination.total_pages) return
            data_state.payers.pagination.current_page = page

            try {
                data_state.payers.is_loading = true

                const response = await companies.getList({
                    page,
                })
                data_state.payers.list = data_state.payers.list.concat(response.data)

                data_state.payers.pagination.total_pages = response.meta.last_page

                data_state.payers.is_failed = false
                data_state.payers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.payers.failure.catchError(error)

                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_failed = true
                }
            } finally {
                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_loading = false
                }
            }
        }

        const searchPayers = (search_query: string) => {
            loadPayers(1, search_query)
        }

        const unshiftFindedPayer = async (company: CompanyT) => {
            await loadPayers(data_state.payers.pagination.current_page)
            data_state.payers.list.unshift(company)
        }

        const getServiceName = (service: EditAdditionalServiceT) => {
            return EditAdditionalServicesEnum[
                service.code as keyof typeof EditAdditionalServicesEnum
            ]
        }

        const isCanRenderDuration = (service: EditAdditionalServiceT) => {
            return ['loader', 'crane', 'manipulator', 'hydraulic_trolley'].includes(service.code)
        }

        const isCanRenderCost = (service: EditAdditionalServiceT) => {
            return ['crane', 'manipulator'].includes(service.code)
        }

        const toggleAddAdditionalService = () => {
            state.is_add_additional_service = !state.is_add_additional_service
        }

        const resetAddAdditionalService = () => {
            state.additional_service_code = null
            toggleAddAdditionalService()
        }

        const addAdditionalService = () => {
            const additional_services = [...BILLING_EDIT_ADDITIONAL_SERVICES]
            const service_found = additional_services.find(
                (item) => item.code === state.additional_service_code
            )

            if (service_found) payload.additionalServices.push(service_found)
            resetAddAdditionalService()
        }

        //

        const getAdditionalServices = computed(() => {
            const additional_services = [...BILLING_EDIT_ADDITIONAL_SERVICES_LIST]
            return additional_services.filter((service) => {
                const service_found = payload.additionalServices.find(
                    (s_f) => s_f.code === service.code
                )

                return !service_found
            })
        })

        const title = computed(() => {
            let titleStr = 'Редактирование вычисления'
            if (props.invoiceNumber) titleStr += ` (Накладная: ${props.invoiceNumber})`
            return titleStr
        })

        //

        onBeforeMount(() => {
            loadCalculation()
            loadAdditionalServices()

            loadCompanies(1)
            loadPayers(1)
        })

        //

        return {
            state,
            payload,
            state_form$,
            data_state,
            //
            emitCancel,
            updateCalculation,
            toggleAddAdditionalService,
            addAdditionalService,
            //
            SHIPMENT_TYPE_OPTIONS,
            ZONE_OPTIONS,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            loadPayers,
            loadMorePayers,
            searchPayers,
            unshiftFindedPayer,
            //
            getServiceName,
            isCanRenderDuration,
            isCanRenderCost,
            title,
            //
            getAdditionalServices,
        }
    },
})
